import React, { useEffect, useState } from "react";
import { Ban, Flag, Share2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useGlobalState } from "../GlobalStates";
import Modal from "../components/common/Modal";
import authAxios from "../authAxios";
import {
  BlockUserConfirmationModal,
  UnblockUserConfirmationModal,
} from "./Connections/components/BlockConfirmationModals";

export const ProfileActionSheetModal = ({
  open,
  onClose,
  userId,
  userName,
  onReportClick,
  onBlockChange,
}) => {
  const { t } = useTranslation();
  const [, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");
  const [user] = useGlobalState("user");

  const [isBlockedByMe, setIsBlockedByMe] = useState(null); // null = loading
  const [blockStatusError, setBlockStatusError] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);

  const isSelf = user?.id && userId && user.id === userId;

  useEffect(() => {
    if (!open || !userId || isSelf) return;
    let cancelled = false;
    setIsBlockedByMe(null);
    setBlockStatusError(false);
    (async () => {
      try {
        const { data } = await authAxios.get(`/v1/social/checkBlockStatus/${userId}`);
        if (!cancelled) setIsBlockedByMe(!!data?.is_blocked_by_me);
      } catch (e) {
        if (!cancelled) {
          setIsBlockedByMe(false);
          setBlockStatusError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, isSelf]);

  const handleShare = () => {
    setOpenAlertBannerMsg(t("profile_link_copied"));
    onClose();
  };

  const handleReport = () => {
    onReportClick();
    onClose();
  };

  const openBlock = () => setShowBlockConfirm(true);
  const openUnblock = () => setShowUnblockConfirm(true);

  const handleConfirmBlock = async () => {
    setIsBlocking(true);
    try {
      await authAxios.post(`/v1/social/blockUser/${userId}`);
      setIsBlockedByMe(true);
      setShowBlockConfirm(false);
      setOpenAlertBannerMsg(
        t("user_blocked_success", { defaultValue: "User blocked" })
      );
      if (onBlockChange) onBlockChange({ blocked: true });
      onClose();
    } catch (e) {
      console.error("Failed to block user:", e);
      setOpenAlertBannerMsg(
        t("user_block_error", { defaultValue: "Could not block user. Please try again." })
      );
    } finally {
      setIsBlocking(false);
    }
  };

  const handleConfirmUnblock = async () => {
    setIsUnblocking(true);
    try {
      await authAxios.post(`/v1/social/unblockUser/${userId}`);
      setIsBlockedByMe(false);
      setShowUnblockConfirm(false);
      setOpenAlertBannerMsg(
        t("user_unblocked_success", { defaultValue: "User unblocked" })
      );
      if (onBlockChange) onBlockChange({ blocked: false });
      onClose();
    } catch (e) {
      console.error("Failed to unblock user:", e);
      setOpenAlertBannerMsg(
        t("user_unblock_error", { defaultValue: "Could not unblock user. Please try again." })
      );
    } finally {
      setIsUnblocking(false);
    }
  };

  const showBlockRow = !isSelf;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        maxWidth="400px"
        maxHeight="auto"
        showCloseButton={false}
        preventOutsideClick={false}
        bodyPadding="tw-p-0"
        snapPoints={({ maxHeight }) => Math.min(maxHeight * 0.5, 280)}
      >
        <div className="tw-py-2">
          <CopyToClipboard text={`${window.location.origin}/profile/${userId}`}>
            <button
              onClick={handleShare}
              className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-border-b tw-border-gray-100 tw-py-3 tw-text-base tw-font-normal tw-text-gray-900 tw-transition-colors hover:tw-bg-gray-50"
            >
              <Share2 className="tw-h-5 tw-w-5 tw-text-gray-700" />
              <span>{t("share_profile")}</span>
            </button>
          </CopyToClipboard>

          {showBlockRow && isBlockedByMe === true && (
            <button
              onClick={openUnblock}
              className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-border-b tw-border-gray-100 tw-py-3 tw-text-base tw-font-normal tw-text-blue-600 tw-transition-colors hover:tw-bg-blue-50"
            >
              <ShieldCheck className="tw-h-5 tw-w-5 tw-text-blue-600" />
              <span>{t("unblock_user", { defaultValue: "Unblock user" })}</span>
            </button>
          )}

          {showBlockRow && isBlockedByMe === false && (
            <button
              onClick={openBlock}
              className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-border-b tw-border-gray-100 tw-py-3 tw-text-base tw-font-normal tw-text-red-600 tw-transition-colors hover:tw-bg-red-50"
            >
              <Ban className="tw-h-5 tw-w-5 tw-text-red-600" />
              <span>{t("block_user", { defaultValue: "Block user" })}</span>
            </button>
          )}

          {showBlockRow && isBlockedByMe === null && !blockStatusError && (
            <div
              className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-border-b tw-border-gray-100 tw-py-3 tw-text-base tw-text-gray-400"
              aria-busy="true"
            >
              <div className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-gray-500" />
            </div>
          )}

          <button
            onClick={handleReport}
            className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-border-b tw-border-gray-100 tw-py-3 tw-text-base tw-font-normal tw-text-red-600 tw-transition-colors hover:tw-bg-red-50"
          >
            <Flag className="tw-h-5 tw-w-5 tw-text-red-600" />
            <span>{t("report_user")}</span>
          </button>
        </div>

        <div className="tw-px-4 tw-pb-4 tw-pt-2">
          <button
            onClick={onClose}
            className="tw-w-full tw-rounded-lg tw-py-2.5 tw-text-base tw-font-semibold tw-text-gray-900 tw-transition-colors hover:tw-bg-gray-100"
          >
            {t("cancel")}
          </button>
        </div>
      </Modal>

      <BlockUserConfirmationModal
        open={showBlockConfirm}
        onClose={() => (isBlocking ? null : setShowBlockConfirm(false))}
        userName={userName}
        onConfirm={handleConfirmBlock}
        isBlocking={isBlocking}
      />

      <UnblockUserConfirmationModal
        open={showUnblockConfirm}
        onClose={() => (isUnblocking ? null : setShowUnblockConfirm(false))}
        userName={userName}
        onConfirm={handleConfirmUnblock}
        isUnblocking={isUnblocking}
      />
    </>
  );
};
